import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { supabaseClient } from '../../services/supabase';
import { commentSafetyService, CommentSafetyCheck } from '../../services/commentSafetyService';
import './Community.css';

interface Comment {
  id: string;
  content: string;
  post_type: 'climate_solution' | 'question' | 'comment' | 'discussion';
  chart_context?: string;
  location_context?: string;
  user_id: string;
  user_email?: string;
  user_location?: string;
  user_type?: string;
  user_first_name?: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  user_profile?: {
    full_name: string;
    email: string;
    role: string;
    user_metadata?: {
      organization?: string;
      location_type?: string;
      specific_location?: string;
    };
  };
  replies?: Comment[];
}

interface NewComment {
  content: string;
  post_type: 'climate_solution' | 'question' | 'comment' | 'discussion';
  chart_context?: string;
  location_context?: string;
  parent_id?: string;
}

const Community: React.FC = () => {
  const { user } = useAuth();
  const { userProfile } = useUserRole();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewCommentForm, setShowNewCommentForm] = useState(false);
  const [newComment, setNewComment] = useState<NewComment>({
    content: '',
    post_type: 'comment',
    location_context: 'General Discussion'
  });
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [safetyCheck, setSafetyCheck] = useState<CommentSafetyCheck | null>(null);
  const [showCAPTCHA, setShowCAPTCHA] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [selectedChartFilter, setSelectedChartFilter] = useState<string>('all');

  const postTypes = [
    { value: 'climate_solution', label: 'Climate Solution', icon: '🌱' },
    { value: 'question', label: 'Question', icon: '❓' },
    { value: 'comment', label: 'Comment', icon: '💬' },
    { value: 'discussion', label: 'Discussion', icon: '🗣️' }
  ];

  // Generate simple CAPTCHA question
  const generateCAPTCHA = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const answer = num1 + num2;
    setCaptchaQuestion(`What is ${num1} + ${num2}?`);
    return answer.toString();
  };

  // Check comment safety before submission
  const checkCommentSafety = async (content: string): Promise<CommentSafetyCheck> => {
    if (!user) {
      return { isAllowed: false, reason: 'Please sign in to comment' };
    }

    const safetyResult = await commentSafetyService.checkCommentSafety(
      user.id,
      content,
      userProfile?.role
    );

    setSafetyCheck(safetyResult);

    if (safetyResult.requiresCAPTCHA) {
      setShowCAPTCHA(true);
      generateCAPTCHA();
    }

    return safetyResult;
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabaseClient
        .from('community_comments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching comments:', error);
        // If table doesn't exist, show a helpful message
        if (error.message.includes('relation "community_comments" does not exist')) {
          setError('Comments feature not yet set up. Please run the database setup script.');
        } else {
          setError('Failed to load comments');
        }
        return;
      }

      // Fetch user profiles for each comment
      const commentsWithProfiles = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: profile } = await supabaseClient
            .from('user_profiles')
            .select('full_name, email, role, user_metadata')
            .eq('id', comment.user_id)
            .single();
          
          return {
            ...comment,
            user_profile: profile
          };
        })
      );

      // Organize comments into threads (top-level comments and their replies)
      const topLevelComments = commentsWithProfiles.filter(comment => comment.parent_id === null);
      const replies = commentsWithProfiles.filter(comment => comment.parent_id !== null);

      const organizedComments = topLevelComments.map(comment => ({
        ...comment,
        replies: replies.filter(reply => reply.parent_id === comment.id)
      }));

      setComments(organizedComments);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!user || !newComment.content.trim()) return;

    try {
      setError(null);
      setSafetyCheck(null);

      // Check comment safety first
      const safetyResult = await checkCommentSafety(newComment.content.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Comment not allowed');
        if (safetyResult.cooldownTime) {
          setTimeout(() => {
            setError(null);
            setSafetyCheck(null);
          }, safetyResult.cooldownTime);
        }
        return;
      }

      // If CAPTCHA is required, verify it
      if (safetyResult.requiresCAPTCHA) {
        if (!showCAPTCHA) {
          // First time CAPTCHA is required - show it
          setShowCAPTCHA(true);
          generateCAPTCHA();
          setError('Please complete CAPTCHA verification');
          return;
        }
        
        // CAPTCHA is shown, verify the answer
        const expectedAnswer = captchaQuestion.split(' ')[2].replace('?', '');
        const userAnswer = captchaValue.trim();
        
        if (userAnswer !== expectedAnswer) {
          setError('CAPTCHA verification failed. Please try again.');
          setCaptchaValue('');
          generateCAPTCHA();
          return;
        }
      }

      // Get user's first name and location from profile
      const firstName = userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
      
      // Use user's profile location if available, otherwise fallback to form location
      const userProfileLocation = user?.user_metadata?.specific_location && user?.user_metadata?.location_type 
        ? `${user.user_metadata.specific_location}, ${user.user_metadata.location_type}`
        : null;
      const userLocation = userProfileLocation || newComment.location_context || 'General Discussion';

      const { error } = await supabaseClient
        .from('community_comments')
        .insert({
          content: newComment.content.trim(),
          post_type: newComment.post_type,
          chart_context: newComment.chart_context,
          location_context: userLocation,
          user_id: user.id,
          user_email: user.email,
          user_location: userLocation,
          user_type: userProfile?.role || 'user',
          user_first_name: firstName,
          parent_id: newComment.parent_id
        });

      if (error) {
        console.error('Error creating comment:', error);
        setError('Failed to create comment');
        return;
      }

      // Record successful comment for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setNewComment({ content: '', post_type: 'comment', location_context: 'General Discussion' });
      setShowNewCommentForm(false);
      setShowCAPTCHA(false);
      setCaptchaValue('');
      setCaptchaQuestion('');
      setSafetyCheck(null);
      await fetchComments();
    } catch (err) {
      console.error('Error creating comment:', err);
      setError('Failed to create comment');
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!user || !replyContent.trim()) return;

    try {
      setError(null);
      setSafetyCheck(null);

      // Check comment safety first
      const safetyResult = await checkCommentSafety(replyContent.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Comment not allowed');
        if (safetyResult.cooldownTime) {
          setTimeout(() => {
            setError(null);
            setSafetyCheck(null);
          }, safetyResult.cooldownTime);
        }
        return;
      }

      // If CAPTCHA is required, verify it
      if (safetyResult.requiresCAPTCHA) {
        if (!showCAPTCHA) {
          // First time CAPTCHA is required - show it
          setShowCAPTCHA(true);
          generateCAPTCHA();
          setError('Please complete CAPTCHA verification');
          return;
        }
        
        // CAPTCHA is shown, verify the answer
        const expectedAnswer = captchaQuestion.split(' ')[2].replace('?', '');
        const userAnswer = captchaValue.trim();
        
        if (userAnswer !== expectedAnswer) {
          setError('CAPTCHA verification failed. Please try again.');
          setCaptchaValue('');
          generateCAPTCHA();
          return;
        }
      }

      // Get user's first name and location from profile
      const firstName = userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
      
      // Use user's profile location if available, otherwise fallback to default
      const userProfileLocation = user?.user_metadata?.specific_location && user?.user_metadata?.location_type 
        ? `${user.user_metadata.specific_location}, ${user.user_metadata.location_type}`
        : null;
      const userLocation = userProfileLocation || 'General Discussion';

      const { error } = await supabaseClient
        .from('community_comments')
        .insert({
          content: replyContent.trim(),
          post_type: 'comment',
          location_context: userLocation,
          user_id: user.id,
          user_email: user.email,
          user_location: userLocation,
          user_type: userProfile?.role || 'user',
          user_first_name: firstName,
          parent_id: parentId
        });

      if (error) {
        console.error('Error creating reply:', error);
        setError('Failed to create reply');
        return;
      }

      // Record successful comment for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setReplyContent('');
      setReplyingTo(null);
      setShowCAPTCHA(false);
      setCaptchaValue('');
      setCaptchaQuestion('');
      setSafetyCheck(null);
      await fetchComments();
    } catch (err) {
      console.error('Error creating reply:', err);
      setError('Failed to create reply');
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!user || !editContent.trim()) return;

    try {
      setError(null);
      setSafetyCheck(null);

      // Check comment safety first
      const safetyResult = await checkCommentSafety(editContent.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Comment not allowed');
        if (safetyResult.cooldownTime) {
          setTimeout(() => {
            setError(null);
            setSafetyCheck(null);
          }, safetyResult.cooldownTime);
        }
        return;
      }

      // If CAPTCHA is required, verify it
      if (safetyResult.requiresCAPTCHA) {
        if (!showCAPTCHA) {
          setShowCAPTCHA(true);
          generateCAPTCHA();
          setError('Please complete CAPTCHA verification');
          return;
        }
        
        const expectedAnswer = captchaQuestion.split(' ')[2].replace('?', '');
        const userAnswer = captchaValue.trim();
        
        if (userAnswer !== expectedAnswer) {
          setError('CAPTCHA verification failed. Please try again.');
          setCaptchaValue('');
          generateCAPTCHA();
          return;
        }
      }

      const { error } = await supabaseClient
        .from('community_comments')
        .update({ 
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .eq('user_id', user.id); // Ensure user can only edit their own comments

      if (error) {
        console.error('Error updating comment:', error);
        setError('Failed to update comment');
        return;
      }

      // Record successful comment for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setEditingComment(null);
      setEditContent('');
      setShowCAPTCHA(false);
      setCaptchaValue('');
      setCaptchaQuestion('');
      setSafetyCheck(null);
      await fetchComments();
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      setError(null);

      const { error } = await supabaseClient
        .from('community_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id); // Ensure user can only delete their own comments

      if (error) {
        console.error('Error deleting comment:', error);
        setError('Failed to delete comment');
        return;
      }

      // Reset form and refresh comments
      setDeletingComment(null);
      await fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    }
  };

  const startEdit = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
    setShowCAPTCHA(false);
    setCaptchaValue('');
    setCaptchaQuestion('');
    setSafetyCheck(null);
  };

  const cancelEdit = () => {
    setEditingComment(null);
    setEditContent('');
    setShowCAPTCHA(false);
    setCaptchaValue('');
    setCaptchaQuestion('');
    setSafetyCheck(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserDisplayName = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_first_name || 
           comment.user_profile?.full_name?.split(' ')[0] || 
           comment.user_email?.split('@')[0] || 
           comment.user_profile?.email?.split('@')[0] || 
           'Anonymous';
  };

  const getUserLocation = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_location || 
           comment.location_context ||
           (comment.user_profile?.user_metadata?.specific_location && comment.user_profile?.user_metadata?.location_type
             ? `${comment.user_profile.user_metadata.specific_location}, ${comment.user_profile.user_metadata.location_type}`
             : comment.user_profile?.user_metadata?.specific_location || 
           comment.user_profile?.user_metadata?.location_type || 
           'Location not specified');
  };

  const getUserType = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_type?.replace('_', ' ').toUpperCase() || 
           comment.user_profile?.role?.replace('_', ' ').toUpperCase() || 
           'USER';
  };

  const getPostTypeInfo = (postType: string) => {
    return postTypes.find(type => type.value === postType) || postTypes[2];
  };

  if (loading) {
    return (
      <div className="community-page">
        <div className="community-loading">
          <div className="loading-spinner"></div>
          <p>Loading community discussions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-page">
      <div className="community-header">
        <h1>Community</h1>
        <p>Share ideas, ask questions, and discuss climate solutions with the community.</p>
      </div>

      {error && (
        <div className="community-error">
          <p>{error}</p>
        </div>
      )}

      {/* Safety Warning */}
      {safetyCheck && !safetyCheck.isAllowed && (
        <div className="community-safety-warning">
          <div className="safety-warning-icon">⚠️</div>
          <div className="safety-warning-text">
            {safetyCheck.reason}
            {safetyCheck.cooldownTime && (
              <div className="cooldown-timer">
                Try again in {Math.ceil(safetyCheck.cooldownTime / 1000)} seconds
              </div>
            )}
          </div>
        </div>
      )}

      {/* CAPTCHA */}
      {showCAPTCHA && (
        <div className="community-captcha">
          <div className="captcha-question">{captchaQuestion}</div>
          <input
            type="text"
            value={captchaValue}
            onChange={(e) => setCaptchaValue(e.target.value)}
            placeholder="Your answer"
            className="captcha-input"
          />
          <div className="captcha-instruction">
            Enter your answer above, then click "Post Comment"
          </div>
        </div>
      )}

      {/* New Comment Form */}
      {user && (
        <div className="new-comment-section">
          <button
            className="new-comment-button"
            onClick={() => setShowNewCommentForm(!showNewCommentForm)}
          >
            {showNewCommentForm ? 'Cancel' : 'Start a Discussion'}
          </button>

          {showNewCommentForm && (
            <div className="new-comment-form">
              <h3>Create New Post</h3>
              <div className="form-group">
                <label>Post Type:</label>
                <select
                  value={newComment.post_type}
                  onChange={(e) => setNewComment({ ...newComment, post_type: e.target.value as any })}
                >
                  {postTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Content:</label>
                <textarea
                  value={newComment.content}
                  onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                  placeholder="Share your thoughts, ask a question, or propose a climate solution..."
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Chart Context (optional):</label>
                <input
                  type="text"
                  value={newComment.chart_context || ''}
                  onChange={(e) => setNewComment({ ...newComment, chart_context: e.target.value })}
                  placeholder="e.g., VMT Chart, GHG Inventory, etc."
                />
              </div>
              <div className="form-group">
                <label>Location Context (optional):</label>
                <input
                  type="text"
                  value={newComment.location_context || ''}
                  onChange={(e) => setNewComment({ ...newComment, location_context: e.target.value })}
                  placeholder="e.g., Berkeley, California, etc."
                />
              </div>
              <div className="form-actions">
                <button onClick={handleSubmitComment} disabled={!newComment.content.trim()}>
                  Post
                </button>
                <button onClick={() => setShowNewCommentForm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comments List */}
      <div className="comments-section">
        <div className="comments-header">
          <h2>Community Discussions</h2>
          <div className="chart-filter">
            <label htmlFor="chart-filter-select">Filter by Chart: </label>
            <select
              id="chart-filter-select"
              value={selectedChartFilter}
              onChange={(e) => setSelectedChartFilter(e.target.value)}
              className="chart-filter-select"
            >
              <option value="all">All Charts</option>
              <option value="VMT Chart">VMT Chart</option>
              <option value="Electricity Consumption">Electricity Consumption</option>
              <option value="Carbon Intensity of Electricity">Carbon Intensity of Electricity</option>
              <option value="Natural Gas Consumption">Natural Gas Consumption</option>
              <option value="Solar PV Installations">Solar PV Installations</option>
              <option value="Heat Pump Adoption">Heat Pump Adoption</option>
              <option value="Inventory Comparison">Inventory Comparison</option>
              <option value="Average Household Carbon Footprint Map">Average Household Carbon Footprint Map</option>
              <option value="Electric Vehicle Adoption">Electric Vehicle Adoption</option>
              <option value="EV Charging Infrastructure">EV Charging Infrastructure</option>
              <option value="EV Adoption Barriers by Income Quintile">EV Adoption Barriers by Income Quintile</option>
              <option value="Means of Transportation to Work">Means of Transportation to Work</option>
              <option value="Health Insurance Coverage">Health Insurance Coverage</option>
              <option value="Educational Attainment">Educational Attainment</option>
              <option value="Population Growth Trends">Population Growth Trends</option>
              <option value="Poverty by Race and Ethnicity">Poverty by Race and Ethnicity</option>
              <option value="Median Household Income by Race">Median Household Income by Race</option>
              <option value="Waste Management & Diversion">Waste Management & Diversion</option>
              <option value="Agriculture">Agriculture</option>
            </select>
          </div>
        </div>
        {comments.length === 0 ? (
          <div className="no-comments">
            <p>No discussions yet. Be the first to start a conversation!</p>
          </div>
        ) : (
          <div className="comments-list">
            {comments
              .filter(comment => {
                if (selectedChartFilter === 'all') return true;
                return comment.chart_context === selectedChartFilter;
              })
              .map(comment => (
              <div key={comment.id} className="comment-card">
                <div className="comment-header">
                  <div className="comment-meta">
                    <span className="post-type-badge">
                      {getPostTypeInfo(comment.post_type).icon} {getPostTypeInfo(comment.post_type).label}
                    </span>
                    <span className="comment-author">{getUserDisplayName(comment)}</span>
                    <span className="comment-location">{getUserLocation(comment)}</span>
                    <span className="comment-user-type">{getUserType(comment)}</span>
                    <span className="comment-date">{formatDate(comment.created_at)}</span>
                  </div>
                </div>
                
                <div className="comment-content">
                  <p>{comment.content}</p>
                  {comment.chart_context && (
                    <div className="comment-context">
                      <strong>Chart:</strong> {comment.chart_context}
                    </div>
                  )}
                  {comment.location_context && (
                    <div className="comment-context">
                      <strong>Location:</strong> {comment.location_context}
                    </div>
                  )}
                </div>

                <div className="comment-actions">
                  {user && (
                    <button
                      className="reply-button"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    >
                      Reply
                    </button>
                  )}
                  {user && user.id === comment.user_id && (
                    <>
                      <button
                        className="edit-button"
                        onClick={() => startEdit(comment)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => setDeletingComment(comment.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>

                {/* Reply Form */}
                {replyingTo === comment.id && user && (
                  <div className="reply-form">
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write your reply..."
                      rows={3}
                    />
                    <div className="reply-actions">
                      <button onClick={() => handleSubmitReply(comment.id)} disabled={!replyContent.trim()}>
                        Reply
                      </button>
                      <button onClick={() => setReplyingTo(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Form */}
                {editingComment === comment.id && user && (
                  <div className="edit-form">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      placeholder="Edit your comment..."
                      rows={3}
                    />
                    {showCAPTCHA && (
                      <div className="community-captcha">
                        <div className="captcha-question">{captchaQuestion}</div>
                        <input
                          type="text"
                          value={captchaValue}
                          onChange={(e) => setCaptchaValue(e.target.value)}
                          placeholder="Your answer"
                          className="captcha-input"
                        />
                        <div className="captcha-instruction">
                          Enter your answer above, then click 'Update Comment'
                        </div>
                      </div>
                    )}
                    <div className="reply-actions">
                      <button 
                        onClick={() => handleEditComment(comment.id)} 
                        disabled={!editContent.trim()}
                      >
                        Update Comment
                      </button>
                      <button onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Delete Confirmation */}
                {deletingComment === comment.id && (
                  <div className="delete-confirmation">
                    <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
                    <div className="reply-actions">
                      <button 
                        className="delete-confirm-button"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        Yes, Delete
                      </button>
                      <button onClick={() => setDeletingComment(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="replies">
                    {comment.replies.map(reply => (
                      <div key={reply.id} className="reply-card">
                        <div className="reply-header">
                          <span className="reply-author">{getUserDisplayName(reply)}</span>
                          <span className="reply-location">{getUserLocation(reply)}</span>
                          <span className="reply-user-type">{getUserType(reply)}</span>
                          <span className="reply-date">{formatDate(reply.created_at)}</span>
                        </div>
                        <div className="reply-content">
                          <p>{reply.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!user && (
        <div className="login-prompt">
          <p>Please sign in to participate in community discussions.</p>
        </div>
      )}
    </div>
  );
};

export { Community };
